import { t } from 'i18next';
import { useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { defaultRenderNewOption, Form, FormField } from 'core/components';
import { InputType } from 'core/enums';
import { FormProvider } from 'core/states';
import { IAutocompleteOption } from 'core/types';
import { PricingProfileType } from 'pricing/enums';
import { pricingProfileService } from 'pricing/services';
import { useProjectPricingProfileActions } from 'projects/hooks';
import { IAddProjectPricingProfileForm } from 'projects/types';

import { CreateProjectPricingProfile } from './CreateProjectPricingProfile';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
}

const schema = () =>
  yup.object({
    pricingProfile: yup.object().label(t('labelPricingProfile')).nullable().required(),
  });

export const LinkProjectPricingProfile = ({ isOpen, onClose, projectId }: IProps) => {
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');

  const { t } = useTranslation();

  const { linkProjectPricingProfile } = useProjectPricingProfileActions();

  const defaultValues = {
    pricingProfile: null,
  };

  const onAddNew = (option: IAutocompleteOption) => {
    setNewProfileName(option.title);
    setIsCreateOpen(true);
  };

  const onAddClose = () => {
    setIsCreateOpen(false);
    setNewProfileName('');
  };

  const onAddSuccess = () => {
    onAddClose();
    onClose();
  };

  const onSubmit =
    (reset: UseFormReset<IAddProjectPricingProfileForm>) =>
    async (data: IAddProjectPricingProfileForm) => {
      const { success } = await linkProjectPricingProfile({
        pricingProfile: data.pricingProfile.id,
        project: projectId,
      });

      if (!success) {
        return;
      }

      setTimeout(() => {
        handleOnClose(reset);
      }, 1000);
    };

  const handleOnClose = (reset?: UseFormReset<IAddProjectPricingProfileForm>) => {
    reset?.(defaultValues);
    onClose();
  };

  return (
    <FormProvider schema={schema()} defaultValues={defaultValues} resetDefaultValues>
      <Form
        isOpen={isOpen}
        onClose={handleOnClose}
        title={t('titleAddProjectPricingProfile')}
        onSubmit={onSubmit}
        submitBtnLabel={t('buttonLabelAdd')}
      >
        <FormField
          name="pricingProfile"
          type={InputType.AUTOCOMPLETE}
          wrapperStyle={{ flex: 1 }}
          inputProps={{
            placeholder: t('placeholderSelectPricingProfile'),
            getOptions: pricingProfileService.getPricingProfileAutocomplete,
            requestPayload: {
              filters: { type: [PricingProfileType.DEFAULT] },
            },
            disableClearable: true,
            hasCreateOption: true,
            onNewOption: onAddNew,
            renderNewOption: (option: IAutocompleteOption) =>
              defaultRenderNewOption(option, t('labelCreateNewPricingProfile')),
          }}
        />
      </Form>

      <CreateProjectPricingProfile
        projectId={projectId}
        isOpen={isCreateOpen}
        onClose={onAddSuccess}
        defaultName={newProfileName}
      />
    </FormProvider>
  );
};
